import cn from 'classnames'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { FinancialCockpitPendingActionOwner } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'

import styles from './AttentionStatusOwnerTabs.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.pendingActions

/** The order the sheet lists the owners in: what the reader owes first, then what others owe. */
export const ATTENTION_OWNER_ORDER = [
  FinancialCockpitPendingActionOwner.Mine,
  FinancialCockpitPendingActionOwner.PBM,
  FinancialCockpitPendingActionOwner.SalesManager,
] as const

/** The owner's own word, so a tab is named by who has to act rather than by an enum value. */
export const getAttentionOwnerLabelKey = (owner: FinancialCockpitPendingActionOwner) =>
  owner === FinancialCockpitPendingActionOwner.Mine
    ? KEYS.ownerTabs.mine
    : `${KEYS.ownerTabs.role}${owner}`

/** Ids the strip and the panel it switches are tied together by; the card owns the prefix. */
export const getAttentionOwnerTabId = (
  idPrefix: string,
  owner: FinancialCockpitPendingActionOwner
) => `${idPrefix}-tab-${owner}`

export const getAttentionOwnerPanelId = (
  idPrefix: string,
  owner: FinancialCockpitPendingActionOwner
) => `${idPrefix}-panel-${owner}`

export interface IAttentionStatusOwnerTab {
  owner: FinancialCockpitPendingActionOwner
  count: number
}

interface IAttentionStatusOwnerTabsProps {
  tabs: IAttentionStatusOwnerTab[]
  activeOwner: FinancialCockpitPendingActionOwner | null
  onSelect: (owner: FinancialCockpitPendingActionOwner) => void
  /** Prefix the card's tab and panel ids are built from — its own `useId`. */
  idPrefix: string
  className?: string
}

/**
 * The owner strip an attention-status detail switches its list with: one tab per side that owes
 * something, each stating how many items it holds.
 *
 * The strip switches lists and nothing else: every act of a card is kept in the card's own corner,
 * so a reader looking for what they may do looks in one place whatever section is on screen.
 *
 * The owners are tabs rather than blocks stacked one under the other. Every owner holds the same
 * kind of row, the reader answers one side at a time, and stacked blocks put the second owner's
 * rows below a list that is already long enough to scroll.
 */
export const AttentionStatusOwnerTabs = ({
  tabs,
  activeOwner,
  onSelect,
  idPrefix,
  className,
}: IAttentionStatusOwnerTabsProps) => {
  const t = useSafetyTranslation()

  return (
    <div
      className={cn(styles.root, className)}
      role="tablist"
      aria-label={t(KEYS.ownerTabs.ariaLabel)}
    >
      {tabs.map(({ owner, count }) => {
        const isActive = owner === activeOwner

        return (
          <button
            key={owner}
            type="button"
            role="tab"
            id={getAttentionOwnerTabId(idPrefix, owner)}
            aria-controls={getAttentionOwnerPanelId(idPrefix, owner)}
            aria-selected={isActive}
            className={cn(styles.tab, { [styles.tabActive]: isActive })}
            onClick={() => onSelect(owner)}
          >
            <Translation span text={getAttentionOwnerLabelKey(owner)} />
            <span className={styles.count}>{count}</span>
          </button>
        )
      })}
    </div>
  )
}

AttentionStatusOwnerTabs.displayName = 'AttentionStatusOwnerTabs'
